import { seekingValues } from '../../components/Profile/Headline/headlineValues';
import { citySelectionFromText } from '../../utils/cityPlace';
import { formatGenreLabel } from '../../utils/genreLabel';

export type IdentityId =
    | 'music-fan'
    | 'performer'
    | 'composer'
    | 'engineer'
    | 'business'
    | 'freelancer';

export interface OnboardingUserTimestamps {
    onboardingCompletedAt?: string | Date | null;
}

export interface NameLocationInput {
    displayName: string;
    locationText: string;
    selectedLocation: { displayText: string; placeId?: string } | null;
}

export interface NameLocationResult {
    ok: boolean;
    error?: string;
    displayName?: string;
    location?: { displayText: string; placeId?: string } | null;
}

export const FAN_STEPS = ['identity', 'influences', 'profile'] as const;
export const CREATOR_STEPS = ['identity', 'skills', 'genres', 'profile'] as const;

/** Chips in the influences picker. Keep in step with backend POPULAR_GENRE_NAMES. */
export const POPULAR_GENRES = [
    'Pop', 'Hip Hop', 'Rock', 'R&B', 'Electronic', 'Country', 'Latin', 'K-Pop',
    'Jazz', 'Classical', 'Metal', 'House', 'Soul', 'Reggae', 'Blues', 'Funk',
    'Folk', 'Punk', 'Gospel', 'Lo-Fi', 'Ambient',
];

export const MAX_TASTE_GENRES = 10;

export interface SuggestionNote {
    requested: string;
    shownAs: string;
    source: 'parent' | 'curated';
}

export function genrePickHint(count: number): string | undefined {
    if (count > MAX_TASTE_GENRES) return `Suggestions use the first ${MAX_TASTE_GENRES}`;
    if (count > 0) return `${count}/${MAX_TASTE_GENRES} picked`;
    return undefined;
}

export function suggestionGenres(genres: string[]): string[] {
    return genres.slice(0, MAX_TASTE_GENRES);
}

interface GenreTaggedArtist {
    genre?: string | null;
    matchedGenres?: string[];
}

function sameGenreName(left: string | null | undefined, right: string): boolean {
    return (left || '').trim().toLowerCase() === right.trim().toLowerCase();
}

/** Selected artists that were suggested for this genre, or that Spotify tags with it. */
export function artistsInGenre<T extends GenreTaggedArtist>(artists: T[], genre: string): T[] {
    const key = genre.trim().toLowerCase();
    if (!key) return [];
    return artists.filter((artist) => (
        sameGenreName(artist.genre, genre)
        || (artist.matchedGenres || []).some((item) => sameGenreName(item, genre))
    ));
}

export function removeGenreArtistsPrompt(count: number, genre: string): string {
    const label = formatGenreLabel(genre);
    const noun = count === 1 ? 'artist' : 'artists';
    return `Also remove ${count} ${label} ${noun}?`;
}

export function popularWithLine(genres: string[]): string | undefined {
    const labels = genres.map(formatGenreLabel).filter(Boolean);
    if (!labels.length) return undefined;
    if (labels.length === 1) return `Popular with ${labels[0]} fans`;
    if (labels.length === 2) return `Popular with ${labels[0]} and ${labels[1]} fans`;
    return 'Popular in the genres you selected.';
}

export function suggestionNoteLine(notes: SuggestionNote[]): string {
    return notes.map((note) => {
        const requested = formatGenreLabel(note.requested);
        const shownAs = formatGenreLabel(note.shownAs);
        return note.source === 'parent'
            ? `Showing ${shownAs} artists for ${requested}.`
            : `Showing a few ${requested} artists we keep on hand.`;
    }).join(' ');
}

export const IDENTITY_ANSWERS: { id: IdentityId; text: string }[] = [
    { id: 'music-fan', text: 'Music Fan' },
    { id: 'performer', text: 'Performer / DJ' },
    { id: 'composer', text: 'Music Creator / Composer' },
    { id: 'engineer', text: 'Music Engineer' },
    { id: 'business', text: 'Business Representative' },
    { id: 'freelancer', text: 'Freelancer' },
];

const SKILLS_BY_IDENTITY: Record<string, string[]> = {
    performer: ['Vocalist', 'Singer', 'Guitarist', 'Bassist', 'Drummer', 'Keyboardist', 'Pianist', 'DJ', 'Promoter'],
    composer: ['Composer', 'Songwriter', 'Producer', 'Beatmaker', 'Promoter'],
    engineer: ['Audio Engineer', 'Mix/Master Engineer', 'Studio Engineer', 'Live Sound', 'Promoter'],
    business: ['Manager', 'Agent', 'Promoter', 'Label', 'Publicist', 'Publisher'],
    freelancer: ['Photographer', 'Videographer', 'Graphic Designer', 'Video Editor', 'Fine Artist', 'Promoter'],
};

export const IDENTITY_DETAILS: Record<string, string> = {
    'music-fan': 'Discover artists and support the scene',
    performer: 'Play, sing, or spin live and on record',
    composer: 'Write, produce, or make beats',
    engineer: 'Record, mix, master, or run live sound',
    business: 'Manage, book, sign, or promote artists',
    freelancer: 'Shoot, design, or edit for musicians',
};

export type HeadlineType = 'Open To' | 'Seeking' | 'Offering';

export const HEADLINE_TYPES: HeadlineType[] = ['Open To', 'Seeking', 'Offering'];

const OPEN_TO_OPTIONS = [
    'Any Collaboration',
    'Remote Collaboration',
    'Local Collaboration',
    'Joining a Band',
    'Starting a Band',
    'Making New Connections',
];

const SEEKING_BY_IDENTITY: Record<string, string[]> = {
    performer: ['Bassist', 'Drummer', 'Guitarist', 'Vocalist', 'Keyboardist', 'Producer'],
    composer: ['Vocalist', 'Rapper', 'Lyricist', 'Producer', 'Mixing Engineer', 'Songwriter'],
    engineer: ['Vocalist', 'Rapper', 'Producer', 'Songwriter', 'Live Artist'],
    business: ['Vocalist', 'Rapper', 'Producer', 'Live Artist', 'Songwriter', 'DJ'],
    freelancer: ['Live Artist', 'Vocalist', 'Rapper', 'DJ', 'Promoter', 'Manager'],
};

/** Every role a Seeking headline can name. Same list the profile headline picker uses. */
export const SEEKING_ROLES = seekingValues;

const HEADLINE_DEFAULTS: Record<string, { type: HeadlineType; content: string }> = {
    performer: { type: 'Open To', content: 'Any Collaboration' },
    composer: { type: 'Offering', content: '' },
    engineer: { type: 'Offering', content: '' },
    business: { type: 'Seeking', content: 'Vocalist' },
    freelancer: { type: 'Offering', content: '' },
};

export function isMusicFan(identity: string | null | undefined): boolean {
    return identity === 'music-fan';
}

export function requiresCreatorSkills(identity: string | null | undefined): boolean {
    return !!identity && !isMusicFan(identity);
}

export function stepsForIdentity(identity: string | null | undefined): string[] {
    if (!identity) return ['identity'];
    return isMusicFan(identity) ? [...FAN_STEPS] : [...CREATOR_STEPS];
}

export function resolveResumeStep(
    identity: string | null | undefined,
    resumeStep: string | null | undefined
): string {
    const steps = stepsForIdentity(identity);
    if (resumeStep && steps.includes(resumeStep)) return resumeStep;
    return steps[0];
}

export function hasFinishedOnboarding(user: OnboardingUserTimestamps | null | undefined): boolean {
    return !!user?.onboardingCompletedAt;
}

/**
 * Own-profile routing. An empty profile is not a reason to redirect.
 * A completed onboarding timestamp means the user stays on the profile.
 * Missing timestamps still need a server check before redirecting.
 */
export function profileRedirectTarget(input: {
    canEdit: boolean;
    onboardingCompletedAt?: string | Date | null;
    serverNeedsOnboarding?: boolean;
}): '/signup' | null {
    if (!input.canEdit) return null;
    if (input.onboardingCompletedAt) return null;
    if (input.serverNeedsOnboarding) return '/signup';
    return null;
}

export function postLoginPath(
    user: OnboardingUserTimestamps | null | undefined,
    fallback: string
): string {
    if (!hasFinishedOnboarding(user)) return '/signup';
    return fallback;
}

export function advanceQuestionIndex(
    questionCount: number,
    currentIndex: number,
    answer: { id: string } | null,
    required: boolean
): { nextIndex: number | null; error: string | null; saveAnswer: boolean; finished: boolean } {
    if (!answer) {
        if (required) {
            return { nextIndex: currentIndex, error: 'Please select an answer', saveAnswer: false, finished: false };
        }
        const finished = currentIndex >= questionCount - 1;
        return {
            nextIndex: finished ? currentIndex : currentIndex + 1,
            error: null,
            saveAnswer: false,
            finished,
        };
    }
    const finished = currentIndex >= questionCount - 1;
    return {
        nextIndex: finished ? currentIndex : currentIndex + 1,
        error: null,
        saveAnswer: true,
        finished,
    };
}

export function resolveNameAndLocation(input: NameLocationInput, validate: (name: string) => { isValid: boolean; error?: string }): NameLocationResult {
    const validation = validate(input.displayName);
    if (!validation.isValid) {
        return { ok: false, error: validation.error || 'Invalid display name' };
    }
    const city = citySelectionFromText(input.locationText, input.selectedLocation);
    if (!city.ok) return { ok: false, error: city.error };
    return {
        ok: true,
        displayName: input.displayName.trim(),
        location: city.location,
    };
}

export function defaultHeadline(identity: string | null | undefined): { type: HeadlineType; content: string } {
    return HEADLINE_DEFAULTS[identity || ''] || { type: 'Open To', content: 'Any Collaboration' };
}

/** Roles this identity is most likely to be looking for. The full catalog stays available beside them. */
export function suggestedSeekingRoles(identity: string | null | undefined): string[] {
    return SEEKING_BY_IDENTITY[identity || ''] || SEEKING_BY_IDENTITY.performer;
}

/**
 * Suggested roles stay first. A search, or showing every role, reaches the rest of the catalog.
 * A role already chosen stays on the short list so the pick does not disappear.
 */
export function visibleSeekingRoles(
    identity: string | null | undefined,
    query: string,
    showAll: boolean,
    selected: string | null,
): string[] {
    const suggested = suggestedSeekingRoles(identity);
    const needle = query.trim().toLowerCase();
    if (needle) {
        return SEEKING_ROLES
            .filter((role) => role.toLowerCase().includes(needle))
            .sort((a, b) => a.localeCompare(b));
    }
    if (!showAll) {
        if (selected && SEEKING_ROLES.includes(selected) && !suggested.includes(selected)) {
            return [...suggested, selected];
        }
        return suggested;
    }
    const suggestedSet = new Set(suggested);
    const rest = SEEKING_ROLES
        .filter((role) => !suggestedSet.has(role))
        .sort((a, b) => a.localeCompare(b));
    return [...suggested, ...rest];
}

/** Offering options are the skills the user picked, so the headline never offers something they do not do. */
export function headlineOptions(identity: string | null | undefined, type: HeadlineType, selectedSkillNames: string[]): string[] {
    if (type === 'Open To') return OPEN_TO_OPTIONS;
    if (type === 'Seeking') return suggestedSeekingRoles(identity);
    return selectedSkillNames;
}

export function resolveHeadlineValue(
    options: string[],
    choice: string | null,
    validValues?: readonly string[],
): string | null {
    const allowed = validValues ?? options;
    if (choice && allowed.includes(choice)) return choice;
    return options[0] ?? null;
}

/** Matches the format AddHeadlineModal saves, so the profile headline parses the same way. */
export function formatHeadline(type: HeadlineType, value: string | null): string | null {
    if (!value) return null;
    return type === 'Offering' ? `Offering ${value} Services` : `${type} ${value}`;
}

export function skillsForIdentity(identity: string | null | undefined): string[] {
    if (!identity || isMusicFan(identity)) return [];
    return SKILLS_BY_IDENTITY[identity] || [];
}

export function capitalizeGenreLabel(value: string): string {
    return formatGenreLabel(value);
}

export function filterGenres(query: string, extra: string[] = []): string[] {
    const pool = Array.from(new Set([...POPULAR_GENRES, ...extra.map(formatGenreLabel)]));
    const needle = query.trim().toLowerCase();
    if (!needle) return POPULAR_GENRES;
    return pool.filter((genre) => genre.toLowerCase().includes(needle));
}

/**
 * Selected genres stay visible while a search narrows the list, so picks are never hidden.
 * Chips already in the list keep their position so nothing moves under the cursor.
 */
export function genreChipList(choices: string[], selected: string[]): string[] {
    const shown = new Set(choices.map((genre) => genre.toLowerCase()));
    return [...selected.filter((genre) => !shown.has(genre.toLowerCase())), ...choices];
}

export function googleDisplayName(user: { displayName?: string | null; firstName?: string | null; lastName?: string | null } | null | undefined): string {
    if (!user) return '';
    if (user.displayName?.trim()) return user.displayName.trim();
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
}
